import React from 'react';
import { FormControl, InputLabel, MenuItem, SelectProps } from '@mui/material';
import { Select } from './styles';
import { ExpandMore } from '@mui/icons-material';
import { HelperText } from '../HelperText';

type ComboBoxProps = {
    items: ComboBoxItems[];
    errorHelperText?: string;
} & SelectProps;

export type ComboBoxItems = {
    label: string;
    value: string;
};

export const ComboBox: React.FC<ComboBoxProps> = ({
    sx,
    value,
    items,
    label,
    disabled: disabledProp,
    errorHelperText,
    MenuProps: menuPropsProp,
    onOpen: onOpenProp,
    ...rest
}) => {
    const id = `${label}-combobox`;
    const disabled = items.length === 0 || disabledProp;
    const hasError = errorHelperText !== undefined;
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [menuWidth, setMenuWidth] = React.useState<number | undefined>();

    const handleOpen: NonNullable<SelectProps['onOpen']> = (event) => {
        const width = containerRef.current?.getBoundingClientRect().width;
        if (width && Number.isFinite(width)) {
            setMenuWidth(width);
        }
        onOpenProp?.(event);
    };

    const mergedMenuProps = React.useMemo(() => {
        const paperProps = menuPropsProp?.PaperProps;

        // Keep existing PaperProps intact, only enforce width to match the select.
        const mergedPaperProps = {
            ...paperProps,
            sx: {
                ...(paperProps as any)?.sx,
                ...(menuWidth


                    ? { width: `${menuWidth}px`, maxWidth: `${menuWidth}px` }
                    : {}),
            },
        };

        return {
            ...menuPropsProp,
            PaperProps: mergedPaperProps,
        };
    }, [menuPropsProp, menuWidth]);

    return (
        <FormControl
            ref={containerRef}
            fullWidth
            size="small"
            sx={sx}
            error={hasError}
        >
            <InputLabel id={id} disabled={disabled}>
                {label}
            </InputLabel>
            <Select
                labelId={id}
                label={label}
                value={value}
                IconComponent={ExpandMore}
                disabled={disabled}
                MenuProps={mergedMenuProps}
                onOpen={handleOpen}
                {...rest}
            >
                {[...items, { label: '', value: '' }].map((item, idx) => (
                    <MenuItem
                        key={idx}
                        value={item.value}
                        style={{ display: item.value === '' ? 'none' : 'flex' }}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </Select>
            <HelperText message={errorHelperText} />
        </FormControl>
    );
};
